import { useCallback, useContext, useEffect, useReducer, useState } from 'react';

import {
	AzureFieldValue,
	IAzureFieldType,
	IAzureProject,
	OptionType
} from '@evinced-private/ui-common';

import { FIELDS_TO_IGNORE, OK_BUTTON_TITLE } from './consts/AzureConstants';
import {
	buildAzureUrl,
	getOverrideFields,
	replaceProjectName,
	updateFieldsFromIssueData
} from './helpers/AzureHelper';
import { logAzureError } from './helpers/logAzureError';
import { AzureTicketDraft } from './types/AzureTicketDraft';
import {
	AzureTicketCreatorContext,
	AzureTicketCreatorContextValue
} from './AzureTicketCreatorProvider';
import {
	azureFormReducer,
	fieldChanged,
	INITIAL_AZURE_FORM_STATE,
	issueDataUpdated,
	issueTypeSelected,
	organizationResolved,
	projectDataLoaded,
	projectSelected
} from './state';

/** Reads the module's config, auth client, REST api and screenshot service from context. */
export const useAzureTicketCreator = (): AzureTicketCreatorContextValue => {
	const value = useContext(AzureTicketCreatorContext);
	if (!value) {
		throw new Error(
			'azure-ticket-creator hooks must be used within an <AzureTicketCreatorProvider>.'
		);
	}
	return value;
};

interface UseAzureIssueFormParams {
	ticketData: AzureTicketDraft;
	bypassAzureProcessRules: boolean;
	closeModal: () => void;
}

interface UseAzureIssueFormResult {
	isLoading: boolean;
	alertModalOpen: boolean;
	appElement?: string;
	buttons: { title: string; onClick: () => void }[];
	host: string;
	projects: IAzureProject[];
	selectedProject: OptionType[];
	issueTypes: OptionType[];
	selectedIssueType: OptionType;
	fields: Record<string, IAzureFieldType>;
	screenshotUrl: string;
	updateField: (newValue: AzureFieldValue, field: IAzureFieldType) => void;
	onChangeProjectPicker: (newProject: OptionType) => Promise<void>;
	onChangeIssueTypePicker: (
		newIssueType: OptionType,
		previousFields: Record<string, IAzureFieldType>
	) => Promise<void>;
	isPublishButtonDisabled: () => boolean;
	onCreateIssueClicked: () => Promise<void>;
}

/**
 * Owns the Azure issue-creation form's data and async orchestration, so the component stays a
 * thin render layer. Loads happen in the flow that triggers them — a single mount effect for the
 * initial org/project resolution, and direct `loadProjectData(host)` calls from the pickers —
 * rather than chaining a second effect that reacts to `host` changing.
 */
export const useAzureIssueForm = ({
	ticketData,
	bypassAzureProcessRules,
	closeModal
}: UseAzureIssueFormParams): UseAzureIssueFormResult => {
	const {
		api,
		screenshot,
		config: { appElement, apiBaseUrl, logger }
	} = useAzureTicketCreator();
	const [isLoading, setLoading] = useState<boolean>(true);
	const [alertModalOpen, setAlertModalOpen] = useState(false);
	const [form, dispatch] = useReducer(azureFormReducer, INITIAL_AZURE_FORM_STATE);
	const { host, projects, selectedProject, issueTypes, selectedIssueType, fields, screenshotUrl } =
		form;

	const updateField = useCallback((newValue: AzureFieldValue, field: IAzureFieldType): void => {
		const updatedField =
			typeof newValue === 'object'
				? { ...field, value: newValue.value }
				: { ...field, value: newValue };

		dispatch(fieldChanged(updatedField));
	}, []);

	// Captures the issue screenshot, swallowing failures so a missing screenshot never blocks
	// issue creation. Returns undefined when capture fails or there is nothing to capture.
	const captureScreenshotSafe = useCallback(
		async (data: AzureTicketDraft): Promise<string | undefined> => {
			try {
				return await screenshot.captureIssueScreenshot(data);
			} catch (error) {
				logAzureError(logger, 'capture screenshot', error);
				return undefined;
			}
		},
		[logger, screenshot]
	);

	const updateIssueData = useCallback(
		async (newFields: Record<string, IAzureFieldType>): Promise<void> => {
			try {
				if (ticketData) {
					const screenshotImageData = await captureScreenshotSafe(ticketData);
					const updatedFields = updateFieldsFromIssueData(newFields, ticketData);

					dispatch(issueDataUpdated(updatedFields, screenshotImageData));
				}
			} catch (error) {
				logAzureError(logger, 'update issue data', error);
			}
		},
		[ticketData, logger, captureScreenshotSafe]
	);

	// Load issue types and fields for a given host. Called directly by the initial load and the
	// project picker (host passed explicitly), so there is no host-reactive effect.
	const loadProjectData = useCallback(
		async (targetHost: string): Promise<void> => {
			if (!targetHost) {
				return;
			}
			setLoading(true);
			try {
				const projectIssueTypes = await api.getProjectTypes(targetHost);
				const formattedProjects = await api.getProjects(targetHost);
				const formattedFields = await api.getProjectFields(targetHost, projectIssueTypes[0]?.label);

				dispatch(
					projectDataLoaded(
						formattedProjects,
						projectIssueTypes,
						projectIssueTypes[0],
						formattedFields
					)
				);
				await updateIssueData(formattedFields);
			} catch (error) {
				logAzureError(logger, 'load project data', error);
			} finally {
				setLoading(false);
			}
		},
		[api, logger, updateIssueData]
	);

	// One-time initialization on mount (modal open === mount): resolve the org/project, then load
	// its data in the same flow. Surfaces the "not connected" alert when no organization exists.
	const initialize = useCallback(async (): Promise<void> => {
		try {
			const organizations = await api.getOrganizations();
			if (!organizations.length) {
				setAlertModalOpen(true);
				return;
			}
			setAlertModalOpen(false);

			const organizationName = organizations[0].label;
			const formattedProjects = await api.getProjects(buildAzureUrl(apiBaseUrl, organizationName));
			const resolvedProject = formattedProjects[0];
			const projectHost = buildAzureUrl(apiBaseUrl, organizationName, resolvedProject?.label);

			dispatch(organizationResolved(projectHost, formattedProjects, [resolvedProject]));
			await loadProjectData(projectHost);
		} catch (error) {
			logAzureError(logger, 'check sign-in', error);
		}
	}, [api, apiBaseUrl, logger, loadProjectData]);

	useEffect(() => {
		initialize();
	}, [initialize]);

	const onChangeProjectPicker = useCallback(
		async (newProject: OptionType): Promise<void> => {
			try {
				const updatedUrl = replaceProjectName(host, newProject.label, apiBaseUrl);
				dispatch(projectSelected(updatedUrl, [newProject]));
				await loadProjectData(updatedUrl);
			} catch (error) {
				logAzureError(logger, 'change project', error);
			}
		},
		[host, apiBaseUrl, logger, loadProjectData]
	);

	const onChangeIssueTypePicker = useCallback(
		async (
			newIssueType: OptionType,
			previousFields: Record<string, IAzureFieldType>
		): Promise<void> => {
			setLoading(true);
			try {
				const newFields = await api.getProjectFields(host, newIssueType.label);
				const overrideFields = getOverrideFields(newFields, previousFields);

				dispatch(issueTypeSelected(newIssueType));
				await updateIssueData(overrideFields);
			} catch (error) {
				logAzureError(logger, 'change issue type', error);
			} finally {
				setLoading(false);
			}
		},
		[api, host, logger, updateIssueData]
	);

	// Disabled while any required field is still empty.
	const isPublishButtonDisabled = useCallback((): boolean => {
		if (fields) {
			return Object.keys(fields).some((fieldKey) => {
				const field = fields[fieldKey];

				if (field.alwaysRequired && !field.value && !FIELDS_TO_IGNORE.includes(field.name)) {
					return true;
				}
				return field.alwaysRequired ? !fields[fieldKey] : false;
			});
		}
		return true;
	}, [fields]);

	const onCreateIssueClicked = useCallback(async (): Promise<void> => {
		try {
			setLoading(true);
			const shouldCloseModal = await api.createIssue(
				host,
				selectedIssueType.label,
				screenshotUrl,
				fields,
				bypassAzureProcessRules
			);

			if (shouldCloseModal) {
				closeModal();
			}
		} catch (error) {
			logAzureError(logger, 'create issue', error);
		} finally {
			setLoading(false);
		}
	}, [
		api,
		host,
		selectedIssueType,
		screenshotUrl,
		fields,
		bypassAzureProcessRules,
		closeModal,
		logger
	]);

	const buttons = [
		{
			title: OK_BUTTON_TITLE,
			onClick: (): void => {
				logger.info('Error popup closed on OK click');
				closeModal();
			}
		}
	];

	return {
		isLoading,
		alertModalOpen,
		appElement,
		buttons,
		host,
		projects,
		selectedProject,
		issueTypes,
		selectedIssueType,
		fields,
		screenshotUrl,
		updateField,
		onChangeProjectPicker,
		onChangeIssueTypePicker,
		isPublishButtonDisabled,
		onCreateIssueClicked
	};
};
